import React, { useState, useEffect } from 'react';

interface VerifiedReview {
  id: string;
  productId: string;
  productName: string;
  vendorId: string;
  vendorName: string;
  testerId: string;
  testerName: string;
  testerLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  rating: number;
  title: string;
  content: string;
  effects: string[];
  dosage: string;
  duration: string;
  verificationBadges: VerificationBadge[];
  submissionDate: string;
  verificationDate: string;
  testingRequest: {
    id: string;
    sampleProvided: boolean;
    trackingNumber?: string;
    verifiedDelivery: boolean;
  };
  integrityScore: number;
  flagged: boolean;
  moderationNotes?: string;
}

interface VerificationBadge {
  type: 'id_verified' | 'trusted_tester' | 'sample_verified' | 'delivery_confirmed' | 'expert_reviewer';
  name: string;
  description: string;
  verifiedDate: string;
}

interface IntegrityAlert {
  id: string;
  type: 'suspicious_pattern' | 'rapid_posting' | 'similar_content' | 'fake_verification' | 'vendor_violation';
  severity: 'low' | 'medium' | 'high' | 'critical';
  reviewId: string;
  description: string;
  autoDetected: boolean;
  resolved: boolean;
  actionTaken?: string;
  createdDate: string;
}

interface ReviewIntegritySystemProps {
  isStaffView?: boolean;
  vendorId?: string;
}

export default function ReviewIntegritySystem({ isStaffView = false, vendorId }: ReviewIntegritySystemProps) {
  const [verifiedReviews, setVerifiedReviews] = useState<VerifiedReview[]>([]);
  const [integrityAlerts, setIntegrityAlerts] = useState<IntegrityAlert[]>([]);
  const [selectedTab, setSelectedTab] = useState<'reviews' | 'alerts' | 'analytics'>('reviews');
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState<string>('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockVerificationBadges: VerificationBadge[] = [
          {
            type: 'id_verified',
            name: 'ID Verified',
            description: 'Government-issued ID confirmed',
            verifiedDate: '2023-08-15'
          },
          {
            type: 'trusted_tester',
            name: 'Trusted Tester',
            description: 'Vetted community reviewer',
            verifiedDate: '2023-08-20'
          },
          {
            type: 'sample_verified',
            name: 'Sample Verified',
            description: 'Product sample confirmed delivered',
            verifiedDate: '2024-01-15'
          },
          {
            type: 'delivery_confirmed',
            name: 'Delivery Confirmed',
            description: 'Tracking number verified delivery',
            verifiedDate: '2024-01-15'
          }
        ];

        const mockReviews: VerifiedReview[] = [
          {
            id: '1',
            productId: 'prod-001',
            productName: 'Premium Red Maeng Da',
            vendorId: 'vendor-001',
            vendorName: 'Premium Kratom Co',
            testerId: 'tester-001',
            testerName: 'Sarah M.',
            testerLevel: 'expert',
            rating: 5,
            title: 'Excellent for Evening Pain Relief',
            content: 'This Red Maeng Da provided exceptional pain relief for my chronic back issues. The effects began within 20 minutes and lasted approximately 4-5 hours. The powder was finely ground with a deep red-green color, indicating proper fermentation. No bitter aftertaste compared to other vendors. Dosage of 4g was perfect for my tolerance level.',
            effects: ['Pain Relief', 'Sedating', 'Mood Enhancement'],
            dosage: '4g',
            duration: '4-5 hours',
            verificationBadges: mockVerificationBadges,
            submissionDate: '2024-01-20',
            verificationDate: '2024-01-20',
            testingRequest: {
              id: 'req-001',
              sampleProvided: true,
              trackingNumber: 'TRK123456789',
              verifiedDelivery: true
            },
            integrityScore: 98,
            flagged: false
          },
          {
            id: '2',
            productId: 'prod-002',
            productName: 'Green Thai Kratom',
            vendorId: 'vendor-001',
            vendorName: 'Premium Kratom Co',
            testerId: 'tester-002',
            testerName: 'Michael R.',
            testerLevel: 'advanced',
            rating: 4,
            title: 'Good Energy Boost, Mild Jitters',
            content: 'Used 3g in the morning for work productivity. Provided clean energy without the crash of caffeine. Some mild jitters at the 2-hour mark, but overall very functional strain. Color was bright green, texture was fine powder. Would recommend for experienced users looking for motivation.',
            effects: ['Energetic', 'Focus', 'Mild Stimulation'],
            dosage: '3g',
            duration: '3-4 hours',
            verificationBadges: mockVerificationBadges.slice(0, 3),
            submissionDate: '2024-01-19',
            verificationDate: '2024-01-19',
            testingRequest: {
              id: 'req-002',
              sampleProvided: true,
              verifiedDelivery: true
            },
            integrityScore: 95,
            flagged: false
          }
        ];

        const mockAlerts: IntegrityAlert[] = [
          {
            id: '1',
            type: 'suspicious_pattern',
            severity: 'medium',
            reviewId: 'review-003',
            description: 'Multiple 5-star reviews from same IP address within 24 hours',
            autoDetected: true,
            resolved: false,
            createdDate: '2024-01-20'
          },
          {
            id: '2',
            type: 'similar_content',
            severity: 'low',
            reviewId: 'review-004',
            description: 'Review content 85% similar to previous review by different user',
            autoDetected: true,
            resolved: true,
            actionTaken: 'Verified unique tester experience',
            createdDate: '2024-01-18'
          }
        ];

        setVerifiedReviews(mockReviews);
        setIntegrityAlerts(mockAlerts);
        setLoading(false);
      }, 1000);
    };

    fetchData();
  }, [vendorId]);

  const getBadgeIcon = (type: string) => {
    switch (type) {
      case 'id_verified': return '🆔';
      case 'trusted_tester': return '⭐';
      case 'sample_verified': return '📦';
      case 'delivery_confirmed': return '✅';
      case 'expert_reviewer': return '🎓';
      default: return '✓';
    }
  };

  const getBadgeColor = (type: string) => {
    switch (type) {
      case 'id_verified': return '#3b82f6';
      case 'trusted_tester': return '#f59e0b';
      case 'sample_verified': return '#8b5cf6';
      case 'delivery_confirmed': return '#10b981';
      case 'expert_reviewer': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getIntegrityColor = (score: number) => {
    if (score >= 95) return '#10b981';
    if (score >= 85) return '#f59e0b';
    if (score >= 70) return '#f97316';
    return '#ef4444';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#d97706';
      case 'low': return '#65a30d';
      default: return '#6b7280';
    }
  };

  const filteredReviews = filterLevel === 'all' 
    ? verifiedReviews 
    : verifiedReviews.filter(review => review.testerLevel === filterLevel);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading review integrity data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Review Integrity System
        </h2>
        <p style={{ color: '#6b7280', fontSize: '1rem' }}>
          Verified reviews with complete transparency and integrity tracking
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem' }}>
            {['reviews', 'alerts', 'analytics'].map(tab => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab as any)}
                style={{
                  padding: '0.75rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: selectedTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                  color: selectedTab === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Verified Reviews Tab */}
      {selectedTab === 'reviews' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
              Verified Reviews ({filteredReviews.length})
            </h3>
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              style={{ 
                padding: '0.5rem', 
                borderRadius: '0.375rem', 
                border: '1px solid #4a5568',
                backgroundColor: '#2d3748',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {filteredReviews.map(review => (
              <div key={review.id} style={{ 
                backgroundColor: '#2d3748', 
                padding: '1.5rem', 
                borderRadius: '0.5rem', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #4a5568'
              }}>
                {/* Review Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ flex: '1' }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>
                      {review.title}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} style={{ color: i < review.rating ? '#f59e0b' : '#e5e7eb', fontSize: '1.25rem' }}>
                            ★
                          </span>
                        ))}
                      </div>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        by {review.testerName}
                      </span>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.125rem 0.375rem', 
                        backgroundColor: '#3b82f620',
                        color: '#3b82f6',
                        borderRadius: '0.25rem',
                        textTransform: 'capitalize'
                      }}>
                        {review.testerLevel}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {review.productName} - {review.vendorName}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '1.25rem', 
                      fontWeight: 'bold', 
                      color: getIntegrityColor(review.integrityScore),
                      marginBottom: '0.25rem'
                    }}>
                      {review.integrityScore}%
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Integrity Score</div>
                  </div>
                </div>

                {/* Verification Badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {review.verificationBadges.map(badge => (
                    <div
                      key={badge.type}
                      title={badge.description}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: getBadgeColor(badge.type) + '20',
                        color: getBadgeColor(badge.type),
                        borderRadius: '0.25rem',
                        fontWeight: '500'
                      }}
                    >
                      <span>{getBadgeIcon(badge.type)}</span>
                      {badge.name}
                    </div>
                  ))}
                </div>

                {/* Review Content */}
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ color: '#a0aec0', fontSize: '0.875rem', lineHeight: '1.5', margin: '0' }}>
                    {review.content}
                  </p>
                </div>

                {/* Review Details */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.375rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
                      Effects
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#a0aec0' }}>
                      {review.effects.join(', ')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
                      Dosage
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#a0aec0' }}>
                      {review.dosage}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
                      Duration
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#a0aec0' }}>
                      {review.duration}
                    </div>
                  </div>
                </div>

                {/* Verification Details */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div>
                    Sample verified: {review.testingRequest.sampleProvided ? 'Yes' : 'No'}
                    {review.testingRequest.trackingNumber && (
                      <span> | Tracking: {review.testingRequest.trackingNumber}</span>
                    )}
                  </div>
                  <div>
                    Verified: {new Date(review.verificationDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Integrity Alerts Tab */}
      {selectedTab === 'alerts' && isStaffView && (
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Integrity Alerts ({integrityAlerts.filter(alert => !alert.resolved).length} active)
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {integrityAlerts.map(alert => (
              <div key={alert.id} style={{ 
                backgroundColor: '#2d3748', 
                padding: '1.5rem', 
                borderRadius: '0.5rem', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: `1px solid ${getSeverityColor(alert.severity)}20`,
                borderLeft: `4px solid ${getSeverityColor(alert.severity)}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: '600', 
                        textTransform: 'uppercase', 
                        color: getSeverityColor(alert.severity) 
                      }}>
                        {alert.severity} {alert.type.replace('_', ' ')}
                      </span>
                      {alert.autoDetected && (
                        <span style={{ 
                          fontSize: '0.75rem', 
                          padding: '0.125rem 0.375rem', 
                          backgroundColor: '#3b82f620',
                          color: '#3b82f6',
                          borderRadius: '0.25rem'
                        }}>
                          Auto-detected
                        </span>
                      )}
                    </div>
                    <p style={{ color: '#a0aec0', margin: '0', fontSize: '0.875rem' }}>
                      {alert.description}
                    </p>
                  </div>
                  
                  <span style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '0.25rem', 
                    fontSize: '0.75rem', 
                    fontWeight: '600',
                    backgroundColor: alert.resolved ? '#10b98120' : '#f59e0b20',
                    color: alert.resolved ? '#10b981' : '#f59e0b'
                  }}>
                    {alert.resolved ? 'Resolved' : 'Active'}
                  </span>
                </div>
                
                {alert.actionTaken && (
                  <div style={{ 
                    padding: '0.75rem', 
                    backgroundColor: '#f0fdf4', 
                    borderRadius: '0.375rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: '600', marginBottom: '0.25rem' }}>
                      Action Taken:
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#166534' }}>
                      {alert.actionTaken}
                    </div>
                  </div>
                )}
                
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  Review ID: {alert.reviewId} | Created: {new Date(alert.createdDate).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {selectedTab === 'analytics' && (
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Review Integrity Analytics
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <div style={{ 
              backgroundColor: '#2d3748', 
              padding: '1.5rem', 
              borderRadius: '0.5rem', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
                Verification Status
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Fully Verified</span>
                  <span style={{ fontWeight: 'bold', color: '#10b981' }}>89%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Partially Verified</span>
                  <span style={{ fontWeight: 'bold', color: '#f59e0b' }}>8%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Unverified</span>
                  <span style={{ fontWeight: 'bold', color: '#ef4444' }}>3%</span>
                </div>
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: '#2d3748', 
              padding: '1.5rem', 
              borderRadius: '0.5rem', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>
                Integrity Scores
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Average Score</span>
                  <span style={{ fontWeight: 'bold', color: '#10b981' }}>94.2</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>High Integrity (90+)</span>
                  <span style={{ fontWeight: 'bold', color: '#10b981' }}>76%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Flagged Reviews</span>
                  <span style={{ fontWeight: 'bold', color: '#ef4444' }}>2%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}